import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { HoldingClass } from 'types';

import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';
import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import i18n from 'i18n/config';

type UseGetHoldingClassOptionsProps = {
  societyId?: string;
  holdingClassId?: string | null;
  partnerId?: string;
};

const FIELDS = 'name sharesCount';

const mapOption = (
  holdingClass: HoldingClass,
  partnerId?: string
): CustomAsyncSelectOption => {
  const label = partnerId
    ? `${holdingClass.name} | ${
        holdingClass.partnerCurrentSharesCount
      } ${i18n.t('shares')}`
    : holdingClass.name;
  return {
    name: holdingClass.name,
    value: holdingClass._id,
    label,
    sharesCount: holdingClass.partnerCurrentSharesCount,
  };
};

const mapOptions = (
  holdingClasses: HoldingClass[],
  partnerId?: string
): CustomAsyncSelectOption[] =>
  holdingClasses.map((holdingClass: HoldingClass) =>
    mapOption(holdingClass, partnerId)
  );

export const useGetHoldingClassOptions = ({
  societyId,
  holdingClassId,
  partnerId,
}: UseGetHoldingClassOptionsProps) => {
  const [selectedHoldingClass, setSelectedHoldingClass] = useState<string>('');
  const [selectedHoldingClassOption, setSelectedHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [holdingClassOptions, setHoldingClassOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleChangeSelectedHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedHoldingClass(selectedOption?.value || '');
    setSelectedHoldingClassOption(selectedOption || null);
  };

  const handleSearchHoldingClassByTerm = async (searchTerm: string) => {
    if (!searchTerm || !societyId) return;

    try {
      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          partnerId,
          params: { fields: FIELDS, searchTerm },
        })
      );

      return mapOptions(holdingClasses, partnerId);
    } catch (error) {
      setIsError(true);
    }
  };

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      if (holdingClassId) {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId,
            partnerId,
          })
        );

        const selectedOption: CustomAsyncSelectOption = mapOption(holdingClass);

        setSelectedHoldingClassOption(selectedOption);
        setIsLoading(false);
        return;
      }

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          partnerId,
          params: { fields: FIELDS },
        })
      );

      const mappedOptions = mapOptions(holdingClasses, partnerId);
      setHoldingClassOptions(mappedOptions);

      const firstOption = mappedOptions[0] || null;
      setSelectedHoldingClass(firstOption.value);
      setSelectedHoldingClassOption(firstOption);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingClassId, partnerId, societyId, i18n.language]);

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  return {
    data: {
      selectedHoldingClass,
      selectedHoldingClassOption,
      holdingClassOptions,
    },
    actions: {
      handleChangeSelectedHoldingClass,
      handleSearchHoldingClassByTerm,
    },
    isLoading,
    isError,
  };
};
